import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import InsideHeader from './InsideHeader';
import ArticleVisual from './ArticleVisual';
import MarkdownRenderer from './MarkdownRenderer';
import useInsideSeo from './useInsideSeo';
import { getAdjacentArticles, getArticle } from './articles';

export default function InsideArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const [markdown, setMarkdown] = useState('');
  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState(0);
  const adjacent = article ? getAdjacentArticles(article.slug) : { previous: null, next: null };

  useInsideSeo({
    title: article?.title || 'Articolo non trovato',
    description: article?.subtitle || 'L’articolo richiesto non è disponibile.',
    path: article ? `/inside/${article.slug}` : window.location.pathname,
    type: 'article',
    article,
    image: article?.image,
    noindex: !article,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!article) { setStatus('missing'); return undefined; }
    const controller = new AbortController();
    setStatus('loading');
    fetch(`/inside-content/${article.file}`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error('Contenuto non disponibile'); return response.text(); })
      .then((text) => { setMarkdown(text); setStatus('ready'); })
      .catch((error) => { if (error.name !== 'AbortError') setStatus('error'); });
    return () => controller.abort();
  }, [article]);

  useEffect(() => {
    const updateProgress = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
    };
    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => { window.removeEventListener('scroll', updateProgress); window.removeEventListener('resize', updateProgress); };
  }, []);

  if (!article || status === 'missing') {
    return <div className="inside-site"><InsideHeader compact /><main className="inside-missing"><span>404</span><h1>Questa sequenza non è in timeline.</h1><Link to="/inside">Torna agli articoli</Link></main></div>;
  }

  return (
    <div className="inside-site inside-article-page">
      <div className="inside-reading-progress" style={{ transform: `scaleX(${progress / 100})` }} />
      <InsideHeader compact />
      <main>
        <article>
          <header className="inside-article-hero">
            <div className="inside-article-heading">
              <Link to="/inside" className="inside-back">← Tutti gli articoli</Link>
              <div className="inside-article-meta"><span>{article.number} / 10</span><span>{article.category}</span><span>{article.readingTime} di lettura</span></div>
              <h1>{article.title}</h1>
              <p>{article.subtitle}</p>
            </div>
            <ArticleVisual article={article} featured />
          </header>

          <div className="inside-article-layout">
            <aside className="inside-article-aside" aria-label="Informazioni articolo">
              <span className="inside-aside-number">{article.number}</span>
              <p>JKL / INSIDE</p><p>Prima serie</p>
            </aside>
            <section className="inside-article-content">
              {status === 'loading' && <div className="inside-loading">Preparazione della timeline…</div>}
              {status === 'error' && <div className="inside-loading">Il contenuto non è stato caricato. Riprova aggiornando la pagina.</div>}
              {status === 'ready' && <MarkdownRenderer markdown={markdown} editorialMode articleNumber={article.number} />}
              {article.sources?.length > 0 && <section className="inside-sources"><h2>Fonti tecniche</h2><ul>{article.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.label} ↗</a></li>)}</ul></section>}
            </section>
          </div>
        </article>


        <section className="inside-article-story">
          <div>
            <p>INSIDE / Redazione aperta</p>
            <h2>Questa storia ne ha fatta venire in mente un’altra?</h2>
            <span>Raccontaci un’esperienza, un ruolo o una dinamica di reparto. Il contributo può essere pubblicato anche in forma anonima.</span>
          </div>
          <Link to="/inside/raccontaci">Raccontaci la tua storia <strong>↗</strong></Link>
        </section>

        <section className="inside-article-cta">
          <p>JKL — The Private Network</p>
          <h2>Il lavoro non finisce nei crediti.</h2>
          <span>Ruoli, competenze, esperienze e disponibilità dentro un network costruito per la post-produzione televisiva.</span>
          <a href="/">Scopri JKL <strong>↗</strong></a>
        </section>

        <nav className="inside-next-nav" aria-label="Articoli precedenti e successivi">
          {adjacent.previous ? <Link to={`/inside/${adjacent.previous.slug}`} className="inside-next-card"><span>← Precedente</span><strong>{adjacent.previous.title}</strong></Link> : <div />}
          {adjacent.next ? <Link to={`/inside/${adjacent.next.slug}`} className="inside-next-card inside-next-card--next"><span>Successivo →</span><strong>{adjacent.next.title}</strong></Link> : <Link to="/inside" className="inside-next-card inside-next-card--next"><span>Fine della serie →</span><strong>Torna agli articoli</strong></Link>}
        </nav>
      </main>
      <footer className="inside-footer"><span>JKL / INSIDE</span><span>Post-produzione televisiva, dall’interno.</span><Link to="/inside/raccontaci">Raccontaci</Link></footer>
    </div>
  );
}
